'use client';

import { useConfig } from '@packages/shared/src/hooks/useConfig/useConfig';
import type { ImageProps } from 'next/image';
import { useRef } from 'react';
import { parseBorderRadius } from '../../utils/borderRadius';
import { Decoline } from '../Decoline/Decoline';
import {
  buildImageSrc,
  extractDimensions,
  extractImageObjects,
  generateAndRenderOptimizedImages,
  renderFallbackImage,
} from './CmsImageItem.helpers';
import { CmsImageItemWrapper } from './CmsImageItemWrapper.client';
import { GroupOnImage } from './GroupOnImage';
import type { CmsImageType } from './types';

export type CmsImageItemProps = {
  data: CmsImageType;
};

export const CmsImageItem = ({ data }: CmsImageItemProps) => {
  const {
    assets: {
      cmsImage: {
        imageOptimizationEnabled,
        uriTransformSource,
        uriTransformEndpoint,
        imageQuality,
      },
    },
  } = useConfig();

  const imgref = useRef<HTMLImageElement>(null);

  const {
    field_display_variant: fieldDisplayVariant,
    field_alternate_text: fieldAltText,
    field_lazyload: lazyLoading = true,
    behavior_paragraph_borderRadius: borderRadius,
    behavior_paragraph_deco_line: hasDecoline,
    paragraph,
    file,
    image_default: imageDefault,
    image_mobile: imageMobile,
  } = data;

  const altText = fieldAltText ?? '';
  const { imageObjDefault, imageObjMobile } = extractImageObjects(file, imageDefault, imageMobile);

  if (!imageObjDefault?.url) {
    return null;
  }

  const isVectorImage = file?.some((f) => f.filemime === 'image/svg+xml') ?? false;
  const { width: defaultWidth, height: defaultHeight } = extractDimensions(imageObjDefault);
  const { width: mobileWidth, height: mobileHeight } = extractDimensions(imageObjMobile);

  const canOptimizeDefault = !!(defaultWidth && defaultHeight && !isVectorImage);
  const canOptimizeMobile = !!(
    mobileWidth &&
    mobileHeight &&
    !isVectorImage &&
    imageObjMobile?.url
  );

  const defaultSrc = buildImageSrc(
    imageObjDefault,
    imageOptimizationEnabled,
    uriTransformSource,
    uriTransformEndpoint,
  );
  const mobileSrc = imageObjMobile
    ? buildImageSrc(
        imageObjMobile,
        imageOptimizationEnabled,
        uriTransformSource,
        uriTransformEndpoint,
      )
    : null;

  const imageStyle = {
    objectFit: 'contain' as const,
    borderRadius: fieldDisplayVariant === 'circle' ? '50%' : borderRadius,
  };

  const commonImageProps: ImageProps = {
    alt: altText,
    loading: lazyLoading ? 'lazy' : 'eager',
    quality: imageOptimizationEnabled ? imageQuality : undefined,
    unoptimized: imageOptimizationEnabled ? undefined : true,
    fetchPriority: lazyLoading ? 'auto' : 'high',
    src: '',
  };

  const wrapperProps = {
    data,
    groupOnImage: paragraph && <GroupOnImage paragraph={paragraph} />,
    imgref,
    isVectorImage,
  };

  const decorLine = hasDecoline ? (
    <Decoline borderRadius={borderRadius ? parseBorderRadius(borderRadius) : undefined} />
  ) : null;

  // Fallback for non-optimizable images
  if (!canOptimizeDefault) {
    return (
      <CmsImageItemWrapper {...wrapperProps}>
        {renderFallbackImage(
          imgref,
          defaultSrc,
          altText,
          lazyLoading,
          fieldDisplayVariant ?? undefined,
          borderRadius ?? undefined,
        )}
        {decorLine}
      </CmsImageItemWrapper>
    );
  }

  // Optimized images
  const imageElement = generateAndRenderOptimizedImages({
    defaultSrc,
    defaultWidth,
    defaultHeight,
    mobileSrc,
    mobileWidth,
    mobileHeight,
    canOptimizeMobile,
    commonImageProps,
    imageStyle,
    imgref,
    altText,
    sizes: data.sizes,
  });

  return (
    <CmsImageItemWrapper {...wrapperProps}>
      {imageElement}
      {decorLine}
    </CmsImageItemWrapper>
  );
};
